import { TValidationResults } from '@verifime/api-definition';
import { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { TOcrData } from './types';

type FormState = {
  formData: TOcrData | null;
  isValid: boolean;
  validationResults: TValidationResults;
};

type FormAction =
  | { type: 'SET_FORM_DATA'; payload: TOcrData | null }
  | { type: 'SET_FORM_DATA_IF_EMPTY'; payload: TOcrData }
  | { type: 'SET_IS_VALID'; payload: boolean }
  | { type: 'SET_VALIDATION_RESULTS'; payload: TValidationResults }
  | { type: 'RESET' };

const initialState: FormState = {
  formData: null,
  isValid: false,
  validationResults: [],
};

/** True when the form holds no values yet: nothing was loaded, scanned or typed. */
export const isFormDataEmpty = (formData: TOcrData | null): boolean =>
  Object.keys(formData ?? {}).length < 1;

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_FORM_DATA':
      return { ...state, formData: action.payload };
    case 'SET_FORM_DATA_IF_EMPTY':
      return isFormDataEmpty(state.formData) ? { ...state, formData: action.payload } : state;
    case 'SET_IS_VALID':
      return { ...state, isValid: action.payload };
    case 'SET_VALIDATION_RESULTS':
      return { ...state, validationResults: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

type FormContextValue = {
  state: FormState;
  setFormData: (data: TOcrData | null) => void;
  /** Sets the form data only if the form holds no values yet, judged when the update is applied. */
  setFormDataIfEmpty: (data: TOcrData) => void;
  setIsValid: (valid: boolean) => void;
  setValidationResults: (results: TValidationResults) => void;
  resetForm: () => void;
};

const FormContext = createContext<FormContextValue | undefined>(undefined);

export const FormProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const setFormData = useCallback(
    (data: TOcrData | null) => {
      dispatch({ type: 'SET_FORM_DATA', payload: data });
    },
    [dispatch],
  );

  const setFormDataIfEmpty = useCallback(
    (data: TOcrData) => {
      dispatch({ type: 'SET_FORM_DATA_IF_EMPTY', payload: data });
    },
    [dispatch],
  );

  const setIsValid = useCallback(
    (valid: boolean) => {
      dispatch({ type: 'SET_IS_VALID', payload: valid });
    },
    [dispatch],
  );

  const setValidationResults = useCallback(
    (results: TValidationResults) => {
      dispatch({ type: 'SET_VALIDATION_RESULTS', payload: results });
    },
    [dispatch],
  );

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const value: FormContextValue = useMemo(
    () => ({
      state,
      setFormData,
      setFormDataIfEmpty,
      setIsValid,
      setValidationResults,
      resetForm,
    }),
    [state],
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export const useFormContext = (): FormContextValue => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

export default FormContext;
